/**
 * Shared visual mockup components used by both the public homepage (Home.jsx)
 * and the logged-in homepage (HomeLoggedIn.jsx).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────────────────────
   BROWSER CHROME WRAPPER
───────────────────────────────────────────────────────────────────────────── */
export const BrowserCard = ({ url = 'teacheslpro.com/pathways', children, className = '' }) => (
  <div className={`hp-browser-card ${className}`}>
    <div className="hp-browser-toolbar">
      <div className="hp-browser-dots">
        <span className="hp-browser-dot hp-browser-dot--red"   />
        <span className="hp-browser-dot hp-browser-dot--yellow"/>
        <span className="hp-browser-dot hp-browser-dot--green" />
      </div>
      <div className="hp-browser-url">{url}</div>
    </div>
    <div className="hp-browser-content">{children}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   PROGRESS RING (SVG)
───────────────────────────────────────────────────────────────────────────── */
export const ProgressRing = ({ pct, label }) => {
  const r   = 26;
  const circ = 2 * Math.PI * r;
  const fill = (pct / 100) * circ;

  return (
    <div className="hp-progress-ring">
      <svg viewBox="0 0 60 60">
        <circle cx="30" cy="30" r={r} className="hp-progress-ring-bg" />
        <circle
          cx="30" cy="30" r={r}
          className="hp-progress-ring-fill"
          strokeDasharray={`${fill} ${circ - fill}`}
          transform="rotate(-90 30 30)"
        />
      </svg>
      <div className="hp-progress-ring-label">
        <span className="hp-progress-ring-pct">{pct}%</span>
        {label && <span className="hp-progress-ring-sub">{label}</span>}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PRODUCT MOCKUP 1 - PATHWAY DASHBOARD
───────────────────────────────────────────────────────────────────────────── */
const students = [
  { level: 'B1', name: 'Zara Paz',     lessons: 1,  total: 96, pct: 1,  mo: '12mo', freq: '2x/week' },
  { level: 'A2', name: 'Philip Olsen', lessons: 26, total: 52, pct: 50, mo: '12mo', freq: '1x/week' },
  { level: 'A1', name: 'Flora Reyes',  lessons: 1,  total: 96, pct: 1,  mo: '12mo', freq: '2x/week' },
];

export const PathwayDashboardMockup = () => (
  <BrowserCard url="teacheslpro.com/pathways">
    <div className="hp-dash-header">
      <div className="hp-dash-search">🔍 Search students…</div>
      <div className="hp-dash-new-btn">+ New Pathway</div>
    </div>
    <div className="hp-dash-tabs">
      {['All', 'Active', 'Completed', 'Draft'].map((t, i) => (
        <div key={t} className={`hp-dash-tab${i === 0 ? ' active' : ''}`}>{t}</div>
      ))}
    </div>
    {students.map(s => (
      <div key={s.name} className="hp-student-card">
        <div className="hp-sc-header">
          <span className="hp-sc-level">{s.level}</span>
          <span className="hp-sc-name">{s.name}</span>
          <span className="hp-sc-status">Active</span>
        </div>
        <div className="hp-sc-progress-bar">
          <div className="hp-sc-progress-fill" style={{ width: `${s.pct}%` }} />
        </div>
        <div className="hp-sc-stats">
          <span>{s.lessons}/{s.total} lessons</span>
          <span>·</span>
          <span>{s.pct}%</span>
          <span>·</span>
          <span>{s.mo}</span>
          <span>·</span>
          <span>{s.freq}</span>
        </div>
      </div>
    ))}
  </BrowserCard>
);

/* ─────────────────────────────────────────────────────────────────────────────
   PRODUCT MOCKUP 2 - FLORA'S PATHWAY DETAIL
───────────────────────────────────────────────────────────────────────────── */
const stages = [
  { name: 'Foundation',             done: 1, total: 24, pct: 4  },
  { name: 'Skill Expansion',        done: 0, total: 24, pct: 0  },
  { name: 'Controlled Fluency',     done: 0, total: 24, pct: 0  },
  { name: 'Independent Performance',done: 0, total: 24, pct: 0  },
];

export const PathwayDetailContent = () => (
  <>
    <div className="hp-pd-header">
      <div className="hp-pd-title">Flora Reyes Learning Pathway</div>
      <div className="hp-pd-subtitle">A1 → Next level</div>
      <div className="hp-pd-actions">
        <span className="hp-pd-action-btn">View Results</span>
        <span className="hp-pd-action-btn">Link Collection</span>
        <span className="hp-pd-action-btn teal">Share</span>
        <span className="hp-pd-action-btn">Regenerate</span>
      </div>
      <div className="hp-pd-collection-banner">
        📁 Collection: Flora Reyes Collection (28 resources)
      </div>
    </div>
    <div className="hp-pd-body">
      <div className="hp-pd-stages">
        {stages.map((st, i) => (
          <div key={st.name} className="hp-pd-stage">
            <span className="hp-pd-chevron">{i === 0 ? '▼' : '▶'}</span>
            <span className="hp-pd-stage-name">{st.name}</span>
            <span className="hp-pd-stage-count">{st.done}/{st.total}</span>
            <div className="hp-pd-stage-bar">
              <div className="hp-pd-stage-fill" style={{ width: `${st.pct}%` }} />
            </div>
            <span className="hp-pd-stage-pct">{st.pct}%</span>
          </div>
        ))}
      </div>
      <div className="hp-pd-sidebar">
        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', alignSelf: 'flex-start' }}>
          Overall Progress
        </div>
        <ProgressRing pct={1} />
        <div className="hp-pd-stat"><strong>1 of 96 lessons</strong></div>
        <div className="hp-pd-stat">Current Level<strong>A1</strong></div>
        <div className="hp-pd-stat">Target Level<strong>Next</strong></div>
        <div className="hp-pd-stat">Timeframe<strong>12mo</strong></div>
        <div className="hp-pd-stat">Lessons/week<strong>2/wk</strong></div>
      </div>
    </div>
  </>
);

export const PathwayDetailMockup = () => (
  <BrowserCard url="teacheslpro.com/pathways/flora">
    <PathwayDetailContent />
  </BrowserCard>
);

/* ─────────────────────────────────────────────────────────────────────────────
   LESSON DETAIL CONTENT
───────────────────────────────────────────────────────────────────────────── */
const lessonRows = [
  {
    title: 'Introduction to Academic English',
    desc: "Basic sentence structure with 'to be'.",
    skill: 'grammar',
    duration: '45min',
    resource: '🔗 Linked',
  },
  {
    title: 'Family Vocabulary',
    desc: 'Learn names of family members.',
    skill: 'vocabulary',
    duration: '45min',
    resource: '',
  },
  {
    title: 'Basic Greetings',
    desc: 'Practice simple greetings and introductions.',
    skill: 'speaking',
    duration: '60min',
    resource: '',
  },
  {
    title: 'Listening for Key Words',
    desc: 'Identify key words in simple dialogues.',
    skill: 'listening',
    duration: '45min',
    resource: '',
  },
  {
    title: 'Reading Simple Texts',
    desc: 'Understand simple texts about everyday topics.',
    skill: 'reading',
    duration: '60min',
    resource: '',
  },
  {
    title: 'Writing Simple Sentences',
    desc: "Construct basic sentences using 'to be'.",
    skill: 'writing',
    duration: '45min',
    resource: '',
  },
];

export const LessonDetailContent = () => (
  <div className="hp-ld-wrapper">
    {/* Main column */}
    <div className="hp-ld-main">
      <div className="hp-ld-stage-header">
        <div className="hp-ld-stage-top">
          <h3 className="hp-ld-stage-title">Foundation</h3>
          <div className="hp-ld-stage-meta">
            <span>0/24 lessons</span>
            <span className="hp-ld-pct">0%</span>
          </div>
        </div>
        <p className="hp-ld-stage-desc">
          This phase focuses on building the basic building blocks of English necessary for academic contexts.
        </p>
        <div className="hp-ld-objectives">
          <strong>Objectives:</strong>
          <ul>
            <li>Develop basic grammar structures.</li>
            <li>Acquire foundational vocabulary for everyday contexts.</li>
            <li>Enhance basic listening and speaking skills.</li>
          </ul>
        </div>
        <div className="hp-ld-topics">
          {['Present Simple', 'To be', 'Articles', 'Family', 'School', 'Food'].map(t => (
            <span key={t} className="hp-ld-topic-pill">{t}</span>
          ))}
        </div>
      </div>

      <div className="hp-ld-lessons">
        {lessonRows.map((lesson, i) => (
          <div key={i} className="hp-ld-lesson-row">
            <span className="hp-ld-drag">⋮⋮</span>
            <span className="hp-ld-check">○</span>
            <div className="hp-ld-lesson-info">
              <strong className="hp-ld-lesson-title">{lesson.title}</strong>
              <span className="hp-ld-lesson-desc">{lesson.desc}</span>
            </div>
            <span className={`hp-ld-skill-tag hp-ld-skill-${lesson.skill}`}>{lesson.skill}</span>
            <span className="hp-ld-duration">{lesson.duration}</span>
            <span className="hp-ld-resource">{lesson.resource}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Sidebar */}
    <div className="hp-ld-sidebar">
      <div className="hp-ld-sidebar-section">
        <div className="hp-ld-sidebar-label">Overall Progress</div>
        <div className="hp-ld-sidebar-ring">
          <ProgressRing pct={0} />
          <span className="hp-ld-ring-sub">0 of 96 lessons</span>
        </div>
      </div>

      <div className="hp-ld-sidebar-section">
        <div className="hp-ld-stat-row">
          <span>Current Level</span><strong>A1</strong>
        </div>
        <div className="hp-ld-stat-row">
          <span>Target Level</span><strong>Next</strong>
        </div>
        <div className="hp-ld-stat-row">
          <span>Timeframe</span><span>12mo <span className="hp-ld-edit">✏️</span></span>
        </div>
        <div className="hp-ld-stat-row">
          <span>Lessons/week</span><span>2/wk <span className="hp-ld-edit">✏️</span></span>
        </div>
      </div>

      <div className="hp-ld-sidebar-section">
        <div className="hp-ld-sidebar-label">Skill Development</div>
        {['Vocabulary', 'Grammar', 'Listening', 'Pronunciation', 'Writing', 'Speaking', 'Reading'].map(skill => (
          <div key={skill} className="hp-ld-skill-row">
            <span className="hp-ld-skill-label">{skill}</span>
            <div className="hp-ld-skill-bar-wrap">
              <div className="hp-ld-skill-bar"><div className="hp-ld-skill-fill" /></div>
            </div>
            <span className="hp-ld-skill-pct">0%</span>
          </div>
        ))}
      </div>

      <div className="hp-ld-sidebar-section">
        <div className="hp-ld-sidebar-label">Student Notes</div>
        <div className="hp-ld-notes-area">Add general notes about this student…</div>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   CURRICULUM TAB SWITCHER
───────────────────────────────────────────────────────────────────────────── */
export const CurriculumTabSwitcher = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="hp-browser-card">
      <div className="hp-browser-toolbar">
        <div className="hp-browser-dots">
          <span className="hp-browser-dot hp-browser-dot--red"   />
          <span className="hp-browser-dot hp-browser-dot--yellow"/>
          <span className="hp-browser-dot hp-browser-dot--green" />
        </div>
        <div className="hp-browser-url">teacheslpro.com/pathways/flora</div>
      </div>
      <div className="hp-tab-bar">
        <button
          className={`hp-tab-btn${activeTab === 0 ? ' active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          Pathway overview
        </button>
        <button
          className={`hp-tab-btn${activeTab === 1 ? ' active' : ''}`}
          onClick={() => setActiveTab(1)}
        >
          Lesson detail
        </button>
      </div>
      <div className="hp-browser-content">
        <div className={`hp-tab-panel${activeTab === 0 ? ' active' : ''}`}>
          <PathwayDetailContent />
        </div>
        <div className={`hp-tab-panel${activeTab === 1 ? ' active' : ''}`}>
          <LessonDetailContent />
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   CURRICULUM CATEGORIES VIEW (flip-card back face)
───────────────────────────────────────────────────────────────────────────── */
export const CurriculumCategoriesView = () => (
  <div className="hp-browser-card">
    <div className="hp-browser-toolbar">
      <div className="hp-browser-dots">
        <span className="hp-browser-dot hp-browser-dot--red"    />
        <span className="hp-browser-dot hp-browser-dot--yellow" />
        <span className="hp-browser-dot hp-browser-dot--green"  />
      </div>
      <div className="hp-browser-url">teacheslpro.com/pathways/flora/overview</div>
    </div>
    <div className="hp-browser-content hp-cat-content">
      <p className="hp-ld-stage-desc">
        This phase focuses on building the basic building blocks of English necessary for academic contexts.
      </p>

      <div className="hp-ld-objectives">
        <strong className="hp-cat-section-heading">⊙ Objectives</strong>
        <ul>
          <li>Develop basic grammar structures.</li>
          <li>Acquire foundational vocabulary for everyday contexts.</li>
          <li>Enhance basic listening and speaking skills.</li>
        </ul>
      </div>

      <div className="hp-ld-topics">
        {['Present Simple', 'To be', 'Articles', 'Family', 'School', 'Food'].map(t => (
          <span key={t} className="hp-ld-topic-pill">{t}</span>
        ))}
      </div>

      <div className="hp-cat-section-heading hp-cat-lessons-heading">⏱ Lessons</div>

      <div className="hp-ld-lessons">
        {lessonRows.slice(0, 4).map((lesson, i) => (
          <div key={i} className="hp-ld-lesson-row hp-cat-lesson-row">
            <span className="hp-ld-drag">⋮⋮</span>
            <span className="hp-ld-check">○</span>
            <div className="hp-ld-lesson-info">
              <strong className="hp-ld-lesson-title">{lesson.title}</strong>
              <span className="hp-ld-lesson-desc">{lesson.desc}</span>
            </div>
            <span className={`hp-ld-skill-tag hp-ld-skill-${lesson.skill}`}>{lesson.skill}</span>
            <span className="hp-ld-duration">{lesson.duration}</span>
            <span className="hp-ld-resource">{lesson.resource}</span>
            <div className="hp-cat-lesson-actions">
              <span className="hp-cat-action">✏️</span>
              <span className="hp-cat-action">→</span>
              <span className="hp-cat-action hp-cat-action--del">🗑</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────────────────────────────────────────── */
export const FAQItem = ({ q, a, open, onToggle }) => (
  <div className="hp-faq-item">
    <button className="hp-faq-question" onClick={onToggle}>
      {q}
      <span className={`hp-faq-chevron${open ? ' open' : ''}`}>▼</span>
    </button>
    <div className={`hp-faq-answer${open ? ' open' : ''}`}>
      <p className="hp-faq-answer-inner">{a}</p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   CATALOGUE RESOURCE DATA
───────────────────────────────────────────────────────────────────────────── */
export const catalogueResources = [
  {
    thumb: 'teal',
    icon: '🦒',
    level: 'A1',
    title: 'Wild Animals: How Many Are There?',
    skills: ['Grammar', 'Vocabulary', 'Speaking'],
    image: '/images/Lesson_preview_1.jpeg',
  },
  {
    thumb: 'orange',
    icon: '🎯',
    level: 'B1',
    title: 'The Decisions That Shape Us: Risk, Regret and Reinvention',
    skills: ['Grammar', 'Reading', 'Speaking'],
    image: '/images/Lesson_preview_2.jpeg?v=20260406c1',
  },
  {
    thumb: 'indigo',
    icon: '🗣️',
    level: 'C2',
    title: 'Free Speech: Where Do We Draw the Line?',
    skills: ['Speaking', 'Vocabulary', 'Listening'],
    image: '/images/Lesson_preview_3.jpeg',
  },
  {
    thumb: 'yellow',
    icon: '🧠',
    level: 'C1',
    title: 'Memory and identity: Are We the Stories We Remember?',
    skills: ['Reading', 'Writing', 'Speaking'],
    image: '/images/Lesson_preview_4.jpeg',
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   RESOURCE PREVIEW MODAL
───────────────────────────────────────────────────────────────────────────── */
export const ResourceModal = ({ resource, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="hp-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="hp-modal" onClick={e => e.stopPropagation()}>

        <button className="hp-modal-close" onClick={onClose} aria-label="Close preview">×</button>

        {/* Header */}
        <div className="hp-modal-header">
          <h3 className="hp-modal-title">{resource.title}</h3>
          <div className="hp-modal-meta-row">
            <span className="hp-modal-level-badge">{resource.level}</span>
            {resource.skills.map(s => (
              <span key={s} className="hp-modal-skill-tag">{s}</span>
            ))}
          </div>
          <p className="hp-modal-sample-note">Sample page - full resource available after free trial</p>
        </div>

        {/* Preview image */}
        <div className="hp-modal-image-wrap">
          <div
            className="hp-modal-image"
            style={{ backgroundImage: `url(${resource.image})` }}
          />
        </div>

        {/* Footer */}
        <div className="hp-modal-footer">
          <p className="hp-modal-footer-heading">Start your free trial to access the full resource</p>
          <div className="hp-modal-footer-btns">
            <Link to="/signup" className="hp-btn hp-btn-primary">Start free trial</Link>
            <Link to="/login" className="hp-btn hp-btn-outline">Log in</Link>
          </div>
          <p className="hp-modal-footer-fine">7-day trial for Standard membership · Cancel any time</p>
        </div>

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   RESOURCE PREVIEW MODAL (logged-in variant - shows "Go to resources")
───────────────────────────────────────────────────────────────────────────── */
export const ResourceModalLoggedIn = ({ resource, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="hp-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="hp-modal" onClick={e => e.stopPropagation()}>

        <button className="hp-modal-close" onClick={onClose} aria-label="Close preview">×</button>

        {/* Header */}
        <div className="hp-modal-header">
          <h3 className="hp-modal-title">{resource.title}</h3>
          <div className="hp-modal-meta-row">
            <span className="hp-modal-level-badge">{resource.level}</span>
            {resource.skills.map(s => (
              <span key={s} className="hp-modal-skill-tag">{s}</span>
            ))}
          </div>
          <p className="hp-modal-sample-note">Sample page - full resource available in the catalogue</p>
        </div>

        {/* Preview image */}
        <div className="hp-modal-image-wrap">
          <div
            className="hp-modal-image"
            style={{ backgroundImage: `url(${resource.image})` }}
          />
        </div>

        {/* Footer */}
        <div className="hp-modal-footer">
          <p className="hp-modal-footer-heading">Access this resource in the catalogue</p>
          <div className="hp-modal-footer-btns">
            <Link to="/resources" className="hp-btn hp-btn-primary" onClick={onClose}>Go to resources</Link>
          </div>
        </div>

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   FAQ DATA
───────────────────────────────────────────────────────────────────────────── */
export const faqs = [
  {
    q: 'How does TeachESLPro help me grow my tutoring business?',
    a: "Planning is unpaid time. TeachESLPro gives every student a placement test and a personalised pathway in minutes, with matching library resources linked in where available, so you get back hours each week to spend on paid lessons or new students. The placement test and shareable pathway also make your service look more professional, which helps turn trial lessons into regular students and keeps students booking for longer.",
  },
  {
    q: 'What exactly is a learning pathway?',
    a: "A learning pathway is a structured sequence of lessons generated for one student, based on their placement results, goals, interests and schedule. It's divided into learning stages (Foundation, Skill Expansion, Controlled Fluency, Independent Performance), each with lessons, overviews and linked catalogue resources. You can customise everything - add or remove lessons, swap resources, or request bespoke materials at any point.",
  },
  {
    q: 'How does the placement test work?',
    a: "You send your student a unique link. They complete it independently - assessing reading, writing and listening - and you receive a clear level breakdown for each skill. You then add your own assessment of their speaking and comprehension before the pathway is generated.",
  },
  {
    q: 'Who creates the bespoke resources?',
    a: "Bespoke resources are created by the TeachESLPro team and delivered within 72 hours of your request. You submit the request directly from the relevant lesson in your student's pathway, providing the topic, level and any specific requirements.",
  },
  {
    q: 'Do bespoke resources stay private to me?',
    a: "You get immediate access to any bespoke resource you request and can re-download it anytime. Seven days after delivery, the resource is added to the main catalogue so other teachers can benefit from it too. This is how we grow a high-quality, hand-made library that reflects what teachers actually need.",
  },
  {
    q: "What's the difference between Standard and Premium?",
    a: "Both plans include the placement test, automated pathway generation and access to the resource library. The main differences are student capacity and downloads: Standard includes 3 student pathways and 8 downloads per month. Premium includes unlimited student pathways, unlimited downloads, and the ability to organise saved resources into per-student collections.",
  },
  {
    q: 'Is there a free trial?',
    a: "Yes - the Standard plan includes a 7-day free trial. The 7-day trial period is for the Standard membership only. You'll have full access to the Standard plan's features, including the placement test and pathway generation, before committing.",
  },
  {
    q: 'Is this suitable for teachers with only a few students?',
    a: "Absolutely. The platform is designed specifically for freelance and private tutors - not language schools. Many teachers manage between 3 and 20 students. The Standard plan at £6.99/month works well for smaller student loads and all plans include the full pathway system.",
  },
  {
    q: 'What counts as a download?',
    a: "Each unique lesson resource you download counts as one download from your monthly quota. Every download includes both the student and teacher versions of the lesson. Once you have downloaded a resource, you can re-download it anytime in the future without it counting against your monthly quota.",
  },
  {
    q: 'Can I cancel anytime?',
    a: "Absolutely. Cancel your subscription at any time from your account settings. You'll have access to our resources under your current subscription terms until the end of your current billing period.",
  },
  {
    q: 'How can I pay for my subscription?',
    a: "Payments on TeachESLPro can be made using a credit or debit card. All payments are processed securely by Stripe and are in GBP.",
  },
  {
    q: 'Can I change my subscription plan later?',
    a: "Yes. You can upgrade or downgrade your subscription anytime by logging into your account and visiting the Subscription page. Any changes will take effect from your next billing date.",
  },
  {
    q: 'Are the lessons in British English or American English?',
    a: "TeachESLPro lessons are written in British English. However, some lessons include audio or video content that may feature other varieties of English, including American English. This helps learners become familiar with different accents and real-world language use.",
  },
];
